import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { API_BASE } from '../config';
import HelpLink from './HelpLink';
import './InlineReviewForm.css';

export const REVIEW_TYPE_OPTIONS = [
  { key: 'property_condition', label: 'Property condition' },
  { key: 'listing_accuracy', label: 'Listing accuracy' },
  { key: 'agent_experience', label: 'Agent/advertiser experience' },
  { key: 'viewing_experience', label: 'Viewing experience' },
  { key: 'location', label: 'Location/environment' },
];

const STAR_VALUES = [1, 2, 3, 4, 5];
const MAX_PHOTOS = 6;

/**
 * Compact, inline "write a review" form for the property page - expands in
 * place within the reviews section, not a modal. See PropertyDetailsPage.js
 * for the trigger button that toggles this open/closed.
 *
 * Doubles as the edit form: pass `existingReview` (the review object as
 * returned by the reviews list, including its `photos` array) to pre-fill
 * every field and switch the submit copy to "Save changes". The parent
 * decides whether `onSubmit` creates or updates - this component doesn't
 * know or care which HTTP verb is behind it.
 */
function PropertyReviewForm({ existingReview, onSubmit, onReviewsChanged, onEditInstead, onCancel }) {
  const { getToken } = useAuth();
  const { showToast } = useToast();
  const isEditing = Boolean(existingReview);

  const [rating, setRating] = useState(existingReview?.rating || 5);
  const [selectedTypes, setSelectedTypes] = useState(existingReview?.review_type || []);
  const [comment, setComment] = useState(existingReview?.comment || '');
  const [didVisit, setDidVisit] = useState(existingReview?.did_visit ?? null);
  const [recommendationChoice, setRecommendationChoice] = useState(
    existingReview?.recommendation_choice ?? null,
  );
  const [isAnonymous, setIsAnonymous] = useState(Boolean(existingReview?.is_anonymous));
  const [existingPhotos, setExistingPhotos] = useState(existingReview?.photos || []);
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Set once the review itself has been saved but its (new) photos failed
  // to upload - the review already exists at this point (text/rating can't
  // be rolled back into the same submit click), so instead of silently
  // dropping the photos or leaving the user stuck, surface an explicit
  // choice: go back in to pick different photos (retrying the identical
  // files that just failed rarely helps - if a file's format/size was the
  // problem, resubmitting it unchanged just fails again), or confirm
  // continuing without photos.
  const [photoUploadFailedForReviewId, setPhotoUploadFailedForReviewId] = useState(null);

  const totalPhotoCount = existingPhotos.length + photos.length;

  const toggleType = (key) => {
    setSelectedTypes((current) =>
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key],
    );
  };

  const handlePhotoSelect = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) {
      return;
    }
    setPhotos((current) => {
      const room = MAX_PHOTOS - existingPhotos.length - current.length;
      const accepted = files.slice(0, Math.max(0, room)).map((file) => ({
        file,
        previewUrl: URL.createObjectURL(file),
      }));
      return [...current, ...accepted];
    });
  };

  const removePhoto = (index) => {
    setPhotos((current) => current.filter((_, i) => i !== index));
  };

  const removeExistingPhoto = async (photoId) => {
    try {
      const res = await fetch(
        `${API_BASE}/sellers/reviews/${existingReview.id}/photos/${photoId}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${getToken()}` } },
      );
      if (!res.ok) {
        throw new Error('Failed to remove photo.');
      }
      setExistingPhotos((current) => current.filter((photo) => photo.id !== photoId));
    } catch (err) {
      showToast(err?.message || 'Failed to remove photo.', 'error');
    }
  };

  const uploadPhotos = async (reviewId) => {
    if (!photos.length) {
      return;
    }
    const formData = new FormData();
    photos.forEach((photo) => formData.append('photos', photo.file));

    const res = await fetch(`${API_BASE}/sellers/reviews/${reviewId}/photos`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getToken()}` },
      body: formData,
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      throw new Error(data?.error || 'Review saved, but photo upload failed.');
    }
    // Some files can succeed while others are skipped for an unsupported
    // format (e.g. an SVG picked alongside PNGs) - that's not a failure of
    // the whole upload, but the user should still know one didn't make it.
    if (data?.skipped?.length) {
      const names = data.skipped.join(', ');
      showToast(
        data.skipped.length === 1
          ? `1 photo (${names}) wasn't uploaded, unsupported format.`
          : `${data.skipped.length} photos (${names}) weren't uploaded, unsupported format.`,
        'warning',
      );
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const trimmedComment = comment.trim();
    if (!trimmedComment) {
      setError('Please share a few words about your experience.');
      return;
    }

    setSubmitting(true);
    let targetReviewId = existingReview?.id || null;
    try {
      const result = await onSubmit({
        rating,
        comment: trimmedComment,
        review_type: selectedTypes,
        did_visit: didVisit,
        recommendation_choice: recommendationChoice,
        is_anonymous: isAnonymous,
      });
      if (!targetReviewId) {
        targetReviewId = result?.id || null;
      }
    } catch (submitError) {
      showToast(submitError?.message || 'Failed to submit review.', 'error');
      setSubmitting(false);
      return;
    }

    if (targetReviewId && photos.length) {
      try {
        await uploadPhotos(targetReviewId);
      } catch (photoError) {
        // The review is already saved at this point - don't pretend the
        // whole submission failed. Ask the user what to do about the
        // photos specifically instead of silently dropping them.
        setPhotoUploadFailedForReviewId(targetReviewId);
        setSubmitting(false);
        return;
      }
    }

    // Re-fetch from the server rather than trust local optimistic state for
    // this - photos are uploaded in a separate step after the review's id
    // comes back, so the parent's optimistic insert can't have known about
    // them ahead of time.
    onReviewsChanged?.();
    onCancel();
  };

  const handleContinueWithoutPhotos = () => {
    showToast(isEditing ? 'Review updated.' : 'Review posted without photos.', 'success');
    onReviewsChanged?.();
    onCancel();
  };

  const handleEditInstead = () => {
    onReviewsChanged?.();
    onEditInstead?.({
      id: photoUploadFailedForReviewId,
      rating,
      comment,
      review_type: selectedTypes,
      did_visit: didVisit,
      recommendation_choice: recommendationChoice,
      is_anonymous: isAnonymous,
      photos: [],
    });
    // Reset local state even though the parent may remount this component
    // with a new `key` (new review -> its id). When editing an *existing*
    // review the id doesn't change, so no remount happens - this is what
    // lets the form fall through to the real, pre-filled fields instead of
    // showing the same failure panel again, and clears the photo that just
    // failed instead of leaving it staged to fail the same way again.
    setPhotoUploadFailedForReviewId(null);
    setPhotos([]);
  };

  if (photoUploadFailedForReviewId) {
    return (
      <div className="inline-review-form-wrap">
        <div className="inline-review-form inline-review-form--notice-row">
          <p className="inline-review-form__notice">
            Your review was {isEditing ? 'updated' : 'posted'}, but the photos couldn&rsquo;t be
            uploaded.
          </p>
          <div className="inline-review-form__actions">
            <button type="button" className="btn btn-ghost" onClick={handleContinueWithoutPhotos}>
              Continue without photos
            </button>
            <button type="button" className="btn btn-primary" onClick={handleEditInstead}>
              Edit review
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="inline-review-form-wrap">
      <form className="inline-review-form" onSubmit={handleSubmit}>
        {error && <p className="inline-review-form__error">{error}</p>}

        <div className="inline-review-form__row">
          <span className="inline-review-form__label">Rating</span>
          <div className="inline-review-form__star-picker" role="radiogroup" aria-label="Rating">
            {STAR_VALUES.map((star) => (
              <button
                key={star}
                type="button"
                className={rating >= star ? '' : 'off'}
                aria-label={`${star} star${star > 1 ? 's' : ''}`}
                aria-pressed={rating === star}
                onClick={() => setRating(star)}
              >
                ★
              </button>
            ))}
            <span className="val">{rating}/5</span>
          </div>
        </div>

        <div className="inline-review-form__row">
          <span className="inline-review-form__label">What are you reviewing?</span>
          <div className="inline-review-form__chip-row">
            {REVIEW_TYPE_OPTIONS.map((option) => {
              const on = selectedTypes.includes(option.key);
              return (
                <button
                  key={option.key}
                  type="button"
                  role="checkbox"
                  aria-checked={on}
                  className={`inline-review-form__chip${on ? ' on' : ''}`}
                  onClick={() => toggleType(option.key)}
                >
                  <span className="dot" aria-hidden="true" />
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="inline-review-form__row">
          <label className="inline-review-form__label" htmlFor="property-review-comment">
            Your review
          </label>
          <textarea
            id="property-review-comment"
            className="inline-review-form__field"
            placeholder="Tell others about your experience..."
            rows={2}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
          />
        </div>

        <div className="inline-review-form__row inline-review-form__compact-row">
          <div>
            <span className="inline-review-form__label">
              Visited? <span className="optional">optional</span>
            </span>
            <div className="inline-review-form__segmented">
              <button
                type="button"
                className={didVisit === true ? 'on' : ''}
                onClick={() => setDidVisit(didVisit === true ? null : true)}
              >
                Yes
              </button>
              <button
                type="button"
                className={didVisit === false ? 'on' : ''}
                onClick={() => setDidVisit(didVisit === false ? null : false)}
              >
                No
              </button>
            </div>
          </div>
          <div>
            <span className="inline-review-form__label">
              Recommend? <span className="optional">optional</span>
            </span>
            <div className="inline-review-form__segmented">
              {['yes', 'no', 'unsure'].map((choice) => (
                <button
                  key={choice}
                  type="button"
                  className={recommendationChoice === choice ? 'on' : ''}
                  onClick={() =>
                    setRecommendationChoice(recommendationChoice === choice ? null : choice)
                  }
                >
                  {choice === 'yes' ? 'Yes' : choice === 'no' ? 'No' : 'Not sure'}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="inline-review-form__row">
          <span className="inline-review-form__label">
            Photos <span className="optional">optional, up to {MAX_PHOTOS}</span>
          </span>
          <div className="inline-review-form__photo-row">
            {existingPhotos.map((photo) => (
              <div key={photo.id} className="inline-review-form__photo-thumb">
                <img src={photo.thumbnail_url || photo.thumbnailUrl || photo.url} alt="" />
                <button
                  type="button"
                  className="inline-review-form__photo-remove"
                  aria-label="Remove photo"
                  onClick={() => removeExistingPhoto(photo.id)}
                >
                  ×
                </button>
              </div>
            ))}
            {photos.map((photo, index) => (
              <div key={photo.previewUrl} className="inline-review-form__photo-thumb">
                <img src={photo.previewUrl} alt="" />
                <button
                  type="button"
                  className="inline-review-form__photo-remove"
                  aria-label="Remove photo"
                  onClick={() => removePhoto(index)}
                >
                  ×
                </button>
              </div>
            ))}
            {totalPhotoCount < MAX_PHOTOS && (
              <label className="inline-review-form__photo-add">
                +
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoSelect}
                  style={{ display: 'none' }}
                />
              </label>
            )}
          </div>
        </div>

        <div className="inline-review-form__footer">
          <label className="inline-review-form__anon-toggle">
            <input
              type="checkbox"
              checked={isAnonymous}
              onChange={(event) => setIsAnonymous(event.target.checked)}
            />
            Post this review anonymously
          </label>
          <p className="inline-review-form__disclaimer">
            By submitting this review, you confirm that your feedback is based on your genuine
            experience. NaijaNest may moderate reviews to remove spam, offensive content, personal
            information, or reviews that violate our{' '}
            <HelpLink to="/help?category=reviews" showArrow={false}>
              guidelines
            </HelpLink>
            .{isAnonymous && ' Your name will not be shown publicly on this review.'}
          </p>
          <div className="inline-review-form__actions">
            <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting
                ? isEditing
                  ? 'Saving…'
                  : 'Submitting…'
                : isEditing
                  ? 'Save changes'
                  : 'Submit review'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default PropertyReviewForm;
